import { useEffect, useState } from "react";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { History as HistoryIcon, ChevronDown, TrendingUp, Clock, Route, Gauge, CheckCircle2, Activity } from "lucide-react";

const fmtVol = (min) => {
  if (!min) return "0 min";
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}h${m ? String(m).padStart(2, "0") : ""}` : `${m} min`;
};
const STATUS_STYLE = {
  "réalisée": "text-[#4A6B53]", "partielle": "text-[#8C6B16]",
};

function Stat({ Icon, value, label, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-obsidian/40"><Icon size={14} strokeWidth={1.6} /><span className="text-[11px] tracking-wide uppercase">{label}</span></div>
      <div className="mt-1 text-lg font-medium tabnum">{value}</div>
    </div>
  );
}

function CycleCard({ c, index }) {
  const [open, setOpen] = useState(index === 0);
  const rate = c.planned ? Math.round((c.completed / c.planned) * 100) : 0;
  return (
    <div data-testid={`history-cycle-${c.cycle_number}`} className="rounded-2xl border border-border bg-white shadow-premium overflow-hidden fade-up">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary/40 transition-[background-color]">
        <div>
          <div className="text-base font-medium">{c.label}</div>
          <div className="text-xs text-obsidian/50 mt-0.5">{c.completed}/{c.planned} séances complétées · {rate}%{c.completed_at ? ` · terminé le ${new Date(c.completed_at).toLocaleDateString("fr-FR")}` : ""}</div>
        </div>
        <ChevronDown size={18} className={`text-obsidian/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <div className="px-5 pb-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat Icon={CheckCircle2} testid={`stat-done-${c.cycle_number}`} value={`${c.completed}/${c.planned}`} label="Complétées" />
        <Stat Icon={Clock} testid={`stat-vol-${c.cycle_number}`} value={fmtVol(c.volume_min)} label="Volume" />
        <Stat Icon={Route} testid={`stat-km-${c.cycle_number}`} value={c.distance_km_est != null ? `~${c.distance_km_est} km` : "—"} label="Distance" />
        <Stat Icon={Gauge} testid={`stat-pace-${c.cycle_number}`} value={c.avg_pace_est || "—"} label="Allure moy." />
      </div>

      <div className="px-5 pt-3 pb-4">
        <div className="flex items-center gap-2 text-sm">
          <TrendingUp size={15} className="text-sage" />
          {c.vma_start != null && c.vma_end != null ? (
            <span data-testid={`stat-vma-${c.cycle_number}`}>
              VMA {c.vma_start} → <strong>{c.vma_end}</strong> km/h
              {c.vma_delta != null && c.vma_delta !== 0 && (
                <span className={c.vma_delta > 0 ? "text-[#4A6B53]" : "text-destructive"}> ({c.vma_delta > 0 ? "+" : ""}{c.vma_delta})</span>
              )}
            </span>
          ) : (
            <span className="text-obsidian/40">VMA non mise à jour sur ce cycle</span>
          )}
        </div>
        <div className="mt-1 text-[11px] text-obsidian/35">Distance et allure estimées à partir de ta VMA.</div>
      </div>

      {open && (
        <div className="border-t border-border bg-secondary/20 divide-y divide-border">
          {c.sessions.length === 0 && <div className="px-5 py-4 text-sm text-obsidian/50">Aucune séance réalisée archivée.</div>}
          {c.sessions.map((s, i) => (
            <div key={i} data-testid={`history-session-${c.cycle_number}-${i}`} className="px-5 py-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.title}</div>
                <div className="text-xs text-obsidian/50">{s.week_label} · {s.day} · {s.duration_min} min · {s.focus}</div>
              </div>
              <span className={`text-xs font-semibold shrink-0 ml-3 ${STATUS_STYLE[s.status] || "text-obsidian/50"}`}>{s.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function History() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/history").then((r) => setData(r.data)).catch(() => setData({ cycles: [] })); }, []);

  return (
    <ClientLayout>
      <div className="pb-10" data-testid="history-page">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-sage/15 flex items-center justify-center"><HistoryIcon size={18} className="text-sage" /></span>
          <div>
            <h1 className="text-2xl font-light tracking-tight">Historique des cycles</h1>
            <p className="text-sm text-obsidian/50">Tes cycles terminés, archivés au fil de ta progression.</p>
          </div>
        </div>

        {!data && <div className="mt-10 pulse-organic w-3 h-3 rounded-full bg-obsidian mx-auto" />}

        {data && data.cycles.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-border bg-white/60 p-10 text-center" data-testid="history-empty">
            <Activity size={28} className="text-obsidian/30 mx-auto" />
            <p className="mt-3 text-sm text-obsidian/60">Aucun cycle terminé pour l'instant.<br />Ton premier cycle apparaîtra ici dès que toutes ses séances seront validées.</p>
          </div>
        )}

        {data && data.cycles.length > 0 && (
          <div className="mt-6 space-y-4">
            {data.cycles.map((c, i) => <CycleCard key={c.cycle_number} c={c} index={i} />)}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
